import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Bell,
  Shield,
  Smartphone,
  Wifi,
  Moon,
  ChevronRight,
  Info,
  LogOut,
  Users,
  Globe,
  User
} from 'lucide-react';
import { mockAPI } from '../../api/mockApi';
import { AllowRemoteControl } from '../../api/types';
import { useUIStore } from '../../stores/uiStore';

export function SettingsScreen() {
  const navigate = useNavigate();
  const { addToast } = useUIStore();
  
  const [notifications, setNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [autoConnect, setAutoConnect] = useState(true);
  const [remoteControls, setRemoteControls] = useState<AllowRemoteControl[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const controls = await mockAPI.getAllowRemoteControl();
      setRemoteControls(controls);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoteControlToggle = async (index: number) => {
    try {
      const control = remoteControls[index];
      const newEnabled = control.isEnabled === true ? false : true;
      await mockAPI.setAllowRemoteControl(
        control.flatId ?? undefined,
        index + 1,
        newEnabled
      );
      
      setRemoteControls(prev => prev.map((c, i) => 
        i === index ? { ...c, isEnabled: !c.isEnabled } : c
      ));
      
      addToast(
        control.isEnabled ? 'Доступ отключён' : 'Доступ включён',
        'success'
      );
    } catch {
      addToast('Ошибка', 'error');
    }
  };

  const menuItems = [
    {
      id: 'notifications',
      icon: Bell,
      label: 'Уведомления',
      toggle: notifications,
      onToggle: () => {
        setNotifications(!notifications);
        addToast(notifications ? 'Отключено' : 'Включено', 'success');
      }
    },
    {
      id: 'security',
      icon: Shield,
      label: 'Безопасность',
      action: () => addToast('В разработке', 'info')
    },
    {
      id: 'devices',
      icon: Smartphone,
      label: 'Устройства',
      action: () => navigate('/pairing')
    },
    {
      id: 'connection',
      icon: Wifi,
      label: 'Автоподключение',
      toggle: autoConnect,
      onToggle: () => {
        setAutoConnect(!autoConnect);
        addToast(autoConnect ? 'Отключено' : 'Включено', 'success');
      }
    },
    {
      id: 'theme',
      icon: Moon,
      label: 'Тёмная тема',
      toggle: darkMode,
      onToggle: () => {
        setDarkMode(!darkMode);
        addToast(darkMode ? 'Светлая' : 'Тёмная', 'success');
      }
    },
    {
      id: 'language',
      icon: Globe,
      label: 'Язык',
      description: 'Русский',
      action: () => addToast('В разработке', 'info')
    },
    {
      id: 'about',
      icon: Info,
      label: 'О приложении',
      description: 'v1.0.0',
      action: () => addToast('Smart Home v1.0.0', 'info')
    }
  ];

  return (
    <div className="min-h-screen pb-24">
      <div className="glass sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-5 py-4">
          <h1 className="text-2xl font-bold text-gradient text-center">Настройки</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-5"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 gradient-primary rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/30">
              <User className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800">Иванов Иван</h2>
              <p className="text-sm text-slate-400">ivanov@example.com</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card overflow-hidden"
        >
          <div className="px-5 py-3 border-b border-white/30">
            <div className="flex items-center gap-2 text-slate-400">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Удалённый доступ</span>
            </div>
          </div>
          
          {isLoading ? (
            <div className="p-5 text-center text-slate-400">Загрузка...</div>
          ) : remoteControls.length === 0 ? (
            <div className="p-5 text-center text-slate-400">Нет пользователей</div>
          ) : (
            <div className="divide-y divide-white/30">
              {remoteControls.map((control, index) => (
                <div key={index} className="px-5 py-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-700">{control.description}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      До: {new Date((control.expiresAt ?? 0) * 1000).toLocaleDateString('ru-RU')}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemoteControlToggle(index)}
                    className={`
                      relative w-12 h-7 rounded-full transition-all duration-300
                      ${control.isEnabled ? 'gradient-primary shadow-lg shadow-indigo-500/30' : 'bg-slate-200'}
                    `}
                  >
                    <motion.div
                      className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md"
                      animate={{ left: control.isEnabled ? '1.25rem' : '0.125rem' }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  </button>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card overflow-hidden"
        >
          <div className="divide-y divide-white/30">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={item.action || item.onToggle}
                className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/30 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-slate-100/80 flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-slate-500" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-slate-700">{item.label}</p>
                    {item.description && (
                      <p className="text-xs text-slate-400">{item.description}</p>
                    )}
                  </div>
                </div>
                
                {item.toggle !== undefined ? (
                  <div
                    className={`
                      relative w-12 h-7 rounded-full transition-all duration-300
                      ${item.toggle ? 'gradient-primary shadow-lg shadow-indigo-500/30' : 'bg-slate-200'}
                    `}
                  >
                    <motion.div
                      className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md"
                      animate={{ left: item.toggle ? '1.25rem' : '0.125rem' }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  </div>
                ) : (
                  <ChevronRight className="w-5 h-5 text-slate-300" />
                )}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          onClick={() => addToast('Выход', 'info')}
          className="w-full glass-card p-4 flex items-center justify-center gap-3 text-rose-500 hover:bg-rose-50/50 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Выйти</span>
        </motion.button>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center text-xs text-slate-400"
        >
          Smart Home v1.0.0
        </motion.p>
      </div>
    </div>
  );
}
